import _ from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import {
    ClientID,
    exchangeToken, exchangeV2Token,
    IAuthFetchOptions,
    IFetchController,
    initCoreConfiguration,
    logoutAndRedirect, removeCodeFromUrl,
    removeHashFromUrl,
    requestAccountAccess
} from './auth.service';
import { AuthStorage } from './auth.storage';
import { ErrorContext } from './error.provider';

export interface IAuthContextProps {
    clientId: ClientID | undefined;
    group: string | undefined;
    setGroup: (group: string, opts?: IAuthFetchOptions) => void;
    cancelGroup: (group: string) => void;
    logoutAndRedirect: (params?: Map<string, string>) => void;
    switchAccount: (accountId: string, href?: string) => void;
    getCurrentAccessToken: () => string | undefined;
    getCurrentRefreshToken: () => string | undefined;
}

export const AuthContext = React.createContext<IAuthContextProps>({
    group: undefined,
    clientId: undefined,
    logoutAndRedirect: () => undefined,
    switchAccount: () => undefined,
    setGroup: () => undefined,
    cancelGroup: () => undefined,
    getCurrentAccessToken: () => undefined,
    getCurrentRefreshToken: () => undefined
});

export const AuthProvider: React.FC<{ clientId: ClientID; children: React.ReactNode }> = ({ children, clientId }): React.ReactElement => {
    const errorContext = useContext(ErrorContext);
    const authStorage = new AuthStorage(clientId);

    //Force V2 disable for everyone
    //authStorage.v2Auth = undefined;

    const [fetchController] = useState<IFetchController>({
        group: '',
        opts: {},
        map: new Map<string, AbortController[]>(),
    });
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        setIsLoading(true);
        (async (): Promise<void> => {
            initCoreConfiguration(authStorage, fetchController, errorContext);
            await handleAuthRedirect();
            setIsLoading(false);
        })();
    }, []);

    // Handle unhandled AbortErrors from canceled requests
    useEffect(() => {
        const handleUnhandledRejection = (event: PromiseRejectionEvent): void => {
            const error = event.reason;
            // Silently ignore AbortErrors - they're expected when canceling requests
            if (
                error &&
                (error.name === 'AbortError' ||
                    (error instanceof Error && error.name === 'AbortError') ||
                    (typeof error === 'object' && error !== null && 'name' in error && error.name === 'AbortError'))
            ) {
                event.preventDefault(); // Prevent the error from appearing in console
                return;
            }
        };

        window.addEventListener('unhandledrejection', handleUnhandledRejection);
        return () => {
            window.removeEventListener('unhandledrejection', handleUnhandledRejection);
        };
    }, []);

    /**
     * Handle redirect from the auth page, exchange and save tokens
     */
    const handleAuthRedirect = async (): Promise<void> => {
        const codeMatch = window.location.hash.match('#code=(.*)');

        const query = new URLSearchParams(location.search);
        const authCode = query.get('code');

        //Allow swapping different auth versions through the querystring for debugging
        const authVersion = query.get('auth_version');
        if(authVersion !== undefined && authVersion !== null && authVersion.length > 0) {
            authStorage.v2Auth = (authVersion === "v2") ? "true": undefined;
        }

        if(codeMatch) {
            removeHashFromUrl();
            const code = codeMatch[1];
            await exchangeToken(code, authStorage);
            // Redirect to main application after successful authentication
            window.location.href = '/';
        } else if(authCode !== undefined && authCode !== null) {
            await exchangeV2Token(authCode as string, authStorage);
            removeCodeFromUrl();
            //authStorage.v2Auth = "true"; //Successfully authing will be set
            // Redirect to main application after successful authentication
            window.location.href = '/';
        }

    };

    const handleLogoutAndRedirect = (params?: Map<string, string>): void => {
        logoutAndRedirect(authStorage, params);
    };

    const handleSwitchAccount = async (accountId: string, href: string = '/'): Promise<void> => {
        // Write the new tokens under the *target* account even if the URL still shows another
        // (provider account switch navigates to `/` after the exchange).
        authStorage.scopeWritesTo(accountId);
        try {
            await requestAccountAccess(accountId, authStorage);
            authStorage.accountId = accountId;
        } finally {
            authStorage.scopeWritesTo(undefined);
        }
        window.location.href = href;
    };

    const handleSetGroup = (grp: string, opts?: IAuthFetchOptions): void => {
        fetchController.group = grp;
        fetchController.opts = opts || {};
    };

    const handleCancelGroup = (group: string): void => {
        const { map } = fetchController;
        const controllers = map.get(group);
        if (controllers) {
            _.forEach(controllers, controller => {
                try {
                    // Only abort if not already aborted
                    if (controller.signal && !controller.signal.aborted) {
                        controller.abort();
                    }
                } catch (err) {
                    // Silently ignore errors from aborting (e.g., already aborted)
                    // AbortErrors are expected and should not be logged
                }
            });
        }
    };

    const getCurrentAccessToken = (): string | undefined => {
        return authStorage.accessToken;
    }

    const getCurrentRefreshToken = (): string | undefined => {
        return authStorage.refreshToken;
    }

    return (
        <AuthContext.Provider
            value={{
                clientId,
                group: fetchController.group,
                logoutAndRedirect: handleLogoutAndRedirect,
                switchAccount: handleSwitchAccount,
                setGroup: handleSetGroup,
                cancelGroup: handleCancelGroup,
                getCurrentAccessToken,
                getCurrentRefreshToken
            }}>
            {!isLoading && children}
        </AuthContext.Provider>
    );
};
